import type { AsyncSelectProps } from "@/components/ui/FormFields/AsyncSelectOptions";
import {
  Badge,
  Button,
  Calendar,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchableSelectFormField,
  Select,
  SelectContent,
  SelectFormField,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import { CalendarIcon, XIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useButton,
  useDatePicker,
  useInteractOutside,
  type DateValue,
} from "react-aria";
import { useForm } from "react-hook-form";
import { useDatePickerState } from "react-stately";

type FilterSelectorFormValues = {
  searchTerm: string;
};

export interface FilterOptionItem {
  group?: string;
  label: string;
  value: string;
  count?: number;
  displayLabel?: ReactNode;
  searchText?: string;
}

type SelectFilterInput = {
  type: "select";
  items: FilterOptionItem[];
  placeholder?: string;
  className?: string;
};

type TextFilterInput = {
  type: "text";
  placeholder?: string;
};

type SearchableSelectFilterInput = AsyncSelectProps & {
  type: "searchable-select";
  items: FilterOptionItem[];
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  emptyMessage?: string;
};

type DateFilterInput = {
  type: "date";
  placeholder?: string;
  conditionPlaceholder?: string;
  conditionOptions?: FilterOptionItem[];
};

// filter inputs are either a select list or a text input
type FilterInputConfig =
  | SelectFilterInput
  | TextFilterInput
  | DateFilterInput
  | SearchableSelectFilterInput;

// Defines a filterable field option (label, value, input config, and optional value label formatter).
export interface FilterOption {
  label: string;
  value: string;
  input: FilterInputConfig;
}

export interface FilterSelectorProps {
  filterOptions: FilterOption[];
  selectedFilters: Partial<Record<string, string[]>>;
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
  primarySelectPlaceholder?: string;
  showHelperText?: boolean;
  selectStyle?: string | null;
  containerClassName?: string;
  fullWidth?: boolean;
  /** Shows a single filter field with its label instead of the filter picker. */
  hidePrimarySelect?: boolean;
  /** Controls whether applied-filter chips are rendered. */
  showActiveFilterBadges?: boolean;
  /** Renders only the applied-filter chips. */
  badgesOnly?: boolean;
}

const humanizeFilterKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const FilterDatePicker = ({
  placeholder,
  onSelect,
  disabled = false,
  joinWithPrevious = false,
  openRequest = 0,
}: {
  placeholder?: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
  joinWithPrevious?: boolean;
  openRequest?: number;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!openRequest || disabled) return;

    // Let the condition menu close and restore focus before opening the calendar.
    const frame = requestAnimationFrame(() => {
      setOpen(true);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [openRequest, disabled]);

  const state = useDatePickerState({
    granularity: "day",
    isDisabled: disabled,
  });

  const {
    groupProps,
    buttonProps: internalButtonProps,
    dialogProps,
    calendarProps,
  } = useDatePicker(
    {
      granularity: "day",
      label: placeholder,
      isDisabled: disabled,
    },
    state,
    ref,
  );

  const { buttonProps } = useButton(internalButtonProps, buttonRef);

  useInteractOutside({
    ref: contentRef,
    onInteractOutside: () => {
      setOpen(false);
    },
  });

  const handleCommit = useCallback(
    (value: DateValue | null) => {
      if (disabled || !value) {
        return;
      }

      const isoValue = value.toString();
      onSelect(isoValue);
      setOpen(false);

      // Delay clearing to allow selection lifecycle to complete
      requestAnimationFrame(() => {
        state.setValue(null);
      });
    },
    [disabled, onSelect, state],
  );

  return (
    <div
      {...groupProps}
      ref={ref}
      className={cn("relative flex items-center", groupProps.className)}
    >
      <Popover
        modal
        open={open}
        onOpenChange={(nextOpen) => {
          if (disabled) {
            setOpen(false);
            return;
          }
          setOpen(nextOpen);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            {...buttonProps}
            ref={buttonRef}
            variant="outline"
            className={cn(
              "border-input h-10 w-10 rounded-md p-0 focus-visible:ring-offset-0 focus-visible:ring-inset",
              joinWithPrevious && "rounded-l-none border-l-0",
            )}
            disabled={disabled || buttonProps.disabled}
            onClick={() => {
              if (disabled) {
                return;
              }
              setOpen(true);
            }}
            aria-label={placeholder ?? "Select date"}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent ref={contentRef} className="w-full">
          <div {...dialogProps}>
            <Calendar
              {...calendarProps}
              isDisabled={disabled || calendarProps.isDisabled}
              onChange={(value) => {
                if (disabled) {
                  return;
                }
                calendarProps.onChange?.(value);
                handleCommit(value);
              }}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const filterSelectValueClasses =
  "h-10 min-w-[8rem] w-full rounded-bl-none rounded-tl-none border-l-transparent focus:border-l focus:border-accent-foreground focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";

const filterControlsContainerClasses = ({
  fullWidth,
  selectStyle,
  containerClassName,
  inputType,
}: {
  fullWidth: boolean;
  selectStyle?: string | null;
  containerClassName?: string;
  inputType?: FilterInputConfig["type"];
}) =>
  cn(
    "flex h-10 w-full shrink-0 items-stretch overflow-hidden",
    fullWidth ? "max-w-full min-w-0" : "min-w-[280px] max-w-[350px]",
    inputType === "select" && selectStyle,
    containerClassName,
  );

export const FilterSelector = ({
  filterOptions,
  selectedFilters,
  onAddFilter,
  onRemoveFilter,
  primarySelectPlaceholder = "Search...",
  showHelperText = true,
  selectStyle = null,
  containerClassName,
  fullWidth = false,
  hidePrimarySelect = false,
  showActiveFilterBadges = true,
  badgesOnly = false,
}: FilterSelectorProps) => {
  const form = useForm<FilterSelectorFormValues>({
    defaultValues: {
      searchTerm: "",
    },
    mode: "onSubmit",
  });

  const [selectedFilterKey, setSelectedFilterKey] = useState(
    () => filterOptions[0]?.value ?? "",
  );

  const hasFilterOptions = filterOptions.length > 0;
  const [dateOpenRequest, setDateOpenRequest] = useState(0);

  const hasActiveFilterBadges = useMemo(
    () =>
      Object.keys(selectedFilters).some(
        (key) => (selectedFilters[key] ?? []).length > 0,
      ),
    [selectedFilters],
  );

  const currentFilterOption = useMemo(
    () => filterOptions.find((option) => option.value === selectedFilterKey),
    [filterOptions, selectedFilterKey],
  );

  const optionByKey = useMemo(() => {
    const m = new Map<string, FilterOption>();
    for (const option of filterOptions) {
      m.set(option.value, option);

      if (option.input.type === "date" && option.input.conditionOptions) {
        option.input.conditionOptions.forEach((condition) => {
          m.set(condition.value, {
            ...option,
            label: `${option.label} (${condition.label})`,
            value: condition.value,
          });
        });
      }
    }
    return m;
  }, [filterOptions]);

  const [dateConditionKey, setDateConditionKey] = useState<string>("");

  useEffect(() => {
    if (!filterOptions.length) {
      return;
    }

    if (!filterOptions.some((option) => option.value === selectedFilterKey)) {
      setSelectedFilterKey(filterOptions[0]?.value ?? "");
      form.reset({ searchTerm: "" });
    }
  }, [filterOptions, form, selectedFilterKey]);

  useEffect(() => {
    form.reset({ searchTerm: "" });
    setDateConditionKey("");
  }, [selectedFilterKey, form]);

  useEffect(() => {
    if (currentFilterOption?.input.type !== "date") {
      setDateConditionKey("");
    }
  }, [currentFilterOption]);

  const applyFilterValue = useCallback(
    (value: string, overrideKey?: string) => {
      const targetKey = overrideKey ?? selectedFilterKey;
      if (!targetKey || !value || value === "null") {
        return;
      }

      onAddFilter(targetKey, value);
      form.reset({ searchTerm: "" });
    },
    [form, onAddFilter, selectedFilterKey],
  );

  const resolveBadgeValue = (key: string, value: string) => {
    const option = optionByKey.get(key);
    if (!option) {
      return value;
    }

    if (option.input.type === "date") {
      return formatDate(value) ?? value;
    }

    if (
      option.input.type === "select" ||
      option.input.type === "searchable-select"
    ) {
      const matchedItem = option.input.items.find(
        (item) => item.value === value,
      );
      if (matchedItem) {
        return matchedItem.label;
      }
    }

    return value;
  };

  const handleFilterSubmit = ({ searchTerm }: FilterSelectorFormValues) => {
    if (!searchTerm || searchTerm === "null") {
      return;
    }

    applyFilterValue(searchTerm);
  };

  const formatFilterItemLabel = (item?: FilterOptionItem) => item?.label;

  const formatSelectedFilterItemLabel = (item?: FilterOptionItem) =>
    item?.label;
  const formatFilterItemSuffix = (item?: FilterOptionItem) => {
    if (!item || typeof item.count !== "number") {
      return undefined;
    }
    return `(${item.count})`;
  };
  const groupedFilterItems =
    currentFilterOption?.input.type === "searchable-select"
      ? Array.from(
          currentFilterOption.input.items.reduce((groups, item) => {
            if (!item.group) return groups;
            const groupItems = groups.get(item.group) ?? [];
            groupItems.push(item);
            groups.set(item.group, groupItems);
            return groups;
          }, new Map<string, FilterOptionItem[]>()),
          ([label, items]) => ({ label, items }),
        )
      : [];

  const renderFilterInput = () => {
    if (!currentFilterOption) {
      return null;
    }

    if (currentFilterOption.input.type === "select") {
      return (
        <div className="flex h-full min-w-[10rem] flex-1 overflow-hidden [&_.space-y-2]:gap-0">
          <div className="h-full w-full">
            <SelectFormField
              register={form.register("searchTerm", { required: true })}
              items={currentFilterOption.input.items}
              placeholder={currentFilterOption.input.placeholder}
              className={cn(
                currentFilterOption.input.className ?? filterSelectValueClasses,
                fullWidth && "w-full",
                hidePrimarySelect && "border-input rounded-md border-l",
              )}
              itemsKey="value"
              formatItemLabel={formatFilterItemLabel}
              formatSelectedItemLabel={formatSelectedFilterItemLabel}
              formatItemSuffix={formatFilterItemSuffix}
              onChange={(value) => {
                if (!value) return;
                applyFilterValue(value);
              }}
            />
          </div>
        </div>
      );
    }

    if (currentFilterOption.input.type === "searchable-select") {
      return (
        <div className="min-w-0 flex-1 overflow-hidden">
          <SearchableSelectFormField
            {...currentFilterOption.input}
            register={form.register("searchTerm", { required: true })}
            items={currentFilterOption.input.items}
            renderItemLabel={(item) => item.displayLabel}
            groupedItems={
              groupedFilterItems.length > 0 ? groupedFilterItems : undefined
            }
            itemsKey="value"
            placeholder={currentFilterOption.input.placeholder}
            formatItemLabel={formatFilterItemLabel}
            formatItemSearchText={(item) => item?.searchText}
            formatSelectedItemLabel={formatSelectedFilterItemLabel}
            formatItemSuffix={formatFilterItemSuffix}
            emptyMessage={currentFilterOption.input.emptyMessage}
            className={cn(
              currentFilterOption.input.className,
              fullWidth && "w-full",
            )}
            buttonClassName={cn(
              hidePrimarySelect
                ? "rounded-md border border-input focus-visible:ring-0 focus-visible:ring-offset-0"
                : "rounded-tl-none rounded-bl-none border-l-transparent focus-visible:ring-0 focus-visible:ring-offset-0",
              currentFilterOption.input.buttonClassName,
              fullWidth && "w-full",
            )}
            onChange={(value) => {
              if (!value) return;
              applyFilterValue(value);
            }}
          />
        </div>
      );
    }

    if (currentFilterOption.input.type === "date") {
      const conditionOptions = currentFilterOption.input.conditionOptions ?? [];
      const conditionPlaceholder =
        currentFilterOption.input.conditionPlaceholder ?? "Select option";

      if (conditionOptions.length > 0) {
        return (
          <div className="flex min-w-0 flex-1 flex-nowrap items-center">
            <Select
              value={dateConditionKey}
              onValueChange={(value) => {
                setDateConditionKey(value);
                setDateOpenRequest((request) => request + 1);
              }}
            >
              <SelectTrigger
                className={cn(
                  "focus:border-accent-foreground h-10 min-w-0 flex-1 rounded-l-md rounded-r-none border-r-0 px-3 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  !dateConditionKey && "text-muted-foreground",
                )}
              >
                <SelectValue placeholder={conditionPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {conditionOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FilterDatePicker
              placeholder={currentFilterOption.input.placeholder}
              disabled={!dateConditionKey}
              openRequest={dateOpenRequest}
              joinWithPrevious
              onSelect={(value) => {
                if (!dateConditionKey) {
                  return;
                }
                applyFilterValue(value, dateConditionKey);
              }}
            />
          </div>
        );
      }

      return (
        <div className="min-w-0 flex-1">
          <FilterDatePicker
            placeholder={currentFilterOption.input.placeholder}
            onSelect={(value) => {
              applyFilterValue(value);
            }}
          />
        </div>
      );
    }

    return (
      <div className="min-w-0 flex-1 overflow-hidden">
        <FormField
          control={form.control}
          name="searchTerm"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder={
                    currentFilterOption.input.placeholder ?? "Search..."
                  }
                  {...field}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    event.stopPropagation();
                    applyFilterValue(event.currentTarget.value);
                  }}
                  className={cn(
                    hidePrimarySelect
                      ? "focus:border-accent-foreground min-w-0 rounded-md focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      : "focus:border-accent-foreground min-w-0 rounded-tl-none rounded-bl-none border-l-transparent focus:border-l focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                    fullWidth && "w-full",
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    );
  };

  const renderFilterControls = () => {
    const controls = (
      <div
        className={filterControlsContainerClasses({
          fullWidth,
          selectStyle,
          containerClassName,
          inputType: currentFilterOption?.input.type,
        })}
      >
        {!hidePrimarySelect ? (
          <Select
            value={selectedFilterKey}
            onValueChange={(value) => {
              setSelectedFilterKey(value);
            }}
            disabled={!hasFilterOptions}
          >
            <SelectTrigger className="focus:border-accent-foreground h-10 max-w-[min(100%,10rem)] min-w-0 shrink-0 rounded-tr-none rounded-br-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 [&>span]:min-w-0 [&>span]:truncate">
              <SelectValue
                placeholder={
                  hasFilterOptions
                    ? primarySelectPlaceholder
                    : "No filters available"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {hasFilterOptions && renderFilterInput()}
      </div>
    );

    if (!hidePrimarySelect) {
      return controls;
    }

    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="text-sm font-medium">
          {currentFilterOption?.label ?? "Filter"}
        </p>
        {controls}
      </div>
    );
  };

  return (
    <Form {...form}>
      <form
        className="m-0"
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await form.handleSubmit(handleFilterSubmit)();
        }}
      >
        {showActiveFilterBadges && hasActiveFilterBadges ? (
          <div className="mb-2 flex w-full min-w-0 flex-wrap gap-2">
            {Object.keys(selectedFilters).flatMap((key) =>
              (selectedFilters[key] ?? []).map((value, index) => {
                const keyLabel =
                  optionByKey.get(key)?.label ?? humanizeFilterKey(key);
                return (
                  <Badge
                    className="h-8 max-w-full min-w-0 gap-1"
                    key={`${keyLabel}: ${value}`}
                  >
                    <span className="min-w-0 flex-1 truncate text-left">
                      {`${keyLabel}: ${resolveBadgeValue(key, value)}`}
                    </span>
                    <XIcon
                      className={cn("mx-2 h-4 shrink-0 cursor-pointer")}
                      data-testid={`remove-filter-${index}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveFilter(key, value);
                      }}
                    />
                  </Badge>
                );
              }),
            )}
          </div>
        ) : null}
        {!badgesOnly && showHelperText ? (
          <div className="flex flex-col gap-2">
            {renderFilterControls()}
            {hasFilterOptions && currentFilterOption?.input.type === "text" && (
              <div className="flex items-center pb-4 pl-1">
                <p className="text-muted-foreground text-xs">
                  Press enter to search
                </p>
              </div>
            )}
            {hasFilterOptions &&
              currentFilterOption?.input.type === "searchable-select" && (
                <div className="pb-4" />
              )}
          </div>
        ) : !badgesOnly ? (
          renderFilterControls()
        ) : null}
      </form>
    </Form>
  );
};

export default FilterSelector;
